import {
  DataLayerContext,
  SummaryStatsType,
} from 'features/dataLayers/DataLayerProvider'
import { useCdfDataFetcher } from 'features/dataLayers/load/useCdfDataFetcher'
import { useCallback, useContext } from 'react'
import { GeckoComponentDataFrame } from 'types'
import { memoizeAsyncSuccess } from 'utils/memoizeAsyncSuccess'

import { CategoricalSummaryStatistics } from './categoricalStatisticsDataType'
import { generateCategoricalDataStatsInWorker } from './generateCategoricalDataStatsInWorker'

/**
 * A hook that returns a function which, when called, fetches the categorical summary statistics for a given CDF.
 */
export function useCategoricalDataSummaryStatsFetcher(): (
  cdf: GeckoComponentDataFrame
) => Promise<CategoricalSummaryStatistics | undefined> {
  const { cdfDataStatsFetchCache } = useContext(DataLayerContext)
  const cdfDataFetcher = useCdfDataFetcher()
  return useCallback(
    async (
      cdf: GeckoComponentDataFrame
    ): Promise<CategoricalSummaryStatistics | undefined> => {
      const cachedValues = cdfDataStatsFetchCache.get(cdf.id)
      if (
        cachedValues &&
        cachedValues.generation === cdf.generation &&
        cachedValues.type === SummaryStatsType.Categorical
      ) {
        return await cachedValues.getData()
      } else if (
        cachedValues?.generation !== cdf.generation ||
        cachedValues.type !== SummaryStatsType.Categorical
      ) {
        cdfDataStatsFetchCache.delete(cdf.id)
      }

      // Must stay a plain async fn -- see `memoizeAsyncSuccess` for why the
      // previous hand-rolled `new Promise` could hang forever.
      async function _getCDFDataFunc(): Promise<
        CategoricalSummaryStatistics | undefined
      > {
        const data = await cdfDataFetcher(cdf)
        if (data === undefined) return undefined
        return await generateCategoricalDataStatsInWorker(data)
      }

      const memoizedGetterFn = memoizeAsyncSuccess(_getCDFDataFunc)

      cdfDataStatsFetchCache.set(cdf.id, {
        generation: cdf.generation,
        getData: memoizedGetterFn,
        type: SummaryStatsType.Categorical,
      })

      return await memoizedGetterFn()
    },
    [cdfDataFetcher, cdfDataStatsFetchCache]
  )
}
